import axios from 'axios';

export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

const MAX_DIMENSION = 1920;
// Base64 adds ~33% and the API accepts JSON bodies up to 10mb
const MAX_UPLOAD_BYTES = 7 * 1024 * 1024;

export const isImageFile = (file) => ACCEPTED_IMAGE_TYPES.split(',').includes(file?.type);

export const formatBytes = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const authConfig = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// Resolve to the response body, or throw with the server's error message
const request = async (pending) => {
  try {
    const { data } = await pending;
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message);
  }
};

const readAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read the image'));
  reader.readAsDataURL(blob);
});

// Scale large photos down and re-encode as WebP so uploads stay small
const compressImage = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const img = new Image();

  img.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not process the image'))),
      'image/webp',
      0.85
    );
  };
  img.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Could not read the image'));
  };

  img.src = objectUrl;
});

// Uploads an image to a console the user owns. `target` is { consoleId } or { consoleItemId }.
// Resolves to the stored asset: { _id, url, publicId, width, height, bytes }.
export const uploadConsoleImage = async (file, target, token) => {
  if (!isImageFile(file)) {
    throw new Error('Please choose a PNG, JPEG, GIF or WebP image.');
  }

  // GIFs are sent untouched so animations survive
  const blob = file.type === 'image/gif' ? file : await compressImage(file);
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error('That image is too large. Please choose one under 7MB.');
  }

  const image = await readAsDataUrl(blob);
  return request(axios.post('/api/console-media/upload', { image, ...target }, authConfig(token)));
};

// A console's uploaded images, each with `usedBy: [{ _id, displayName }]`
export const listConsoleMedia = (consoleId, token) => (
  request(axios.get(`/api/console-media/console/${consoleId}`, authConfig(token)))
);

export const deleteConsoleMedia = (assetId, token) => (
  request(axios.delete(`/api/console-media/${assetId}`, authConfig(token)))
);

// Admin: { scanned, orphans: [{ publicId, url, bytes, ... }], totalBytes }
export const scanOrphanedImages = (token) => (
  request(axios.get('/api/console-media/admin/orphans', authConfig(token)))
);

// Admin: { deleted: [publicId], skipped: [publicId] }
export const deleteOrphanedImages = (publicIds, token) => (
  request(axios.post('/api/console-media/admin/orphans/delete', { publicIds }, authConfig(token)))
);
